import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface ActivityLog {
  id: string;
  action: string;
  details: any;
  created_at: string;
  user_id: string | null;
}

interface ClientHistoryTabProps {
  clientId: string;
}

export default function ClientHistoryTab({ clientId }: ClientHistoryTabProps) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchActivityLog();
  }, [clientId]);

  const fetchActivityLog = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('activity_log')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error fetching activity log:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatChanges = (log: ActivityLog) => {
    const details = log.details;
    
    if (details?.changes) {
      const changes = Object.entries(details.changes)
        .map(([field, values]: [string, any]) => {
          const oldVal = values.old !== null && values.old !== undefined ? JSON.stringify(values.old) : 'empty';
          const newVal = values.new !== null && values.new !== undefined ? JSON.stringify(values.new) : 'empty';
          return `${field}: ${oldVal} → ${newVal}`;
        })
        .join(', ');
      return changes;
    }
    
    return JSON.stringify(details);
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      'client_updated': 'Updated',
      'client_created': 'Created',
      'note_added': 'Note Added',
      'appointment_scheduled': 'Appointment Scheduled',
      'document_uploaded': 'Document Uploaded',
    };
    return labels[action] || action;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No activity history yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="font-medium">All changes to this client profile</h4>
      
      <div className="space-y-3">
        {logs.map((log) => (
          <div key={log.id} className="p-4 border rounded-md space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">
                {getActionLabel(log.action)}
              </span>
              <span className="text-xs text-muted-foreground" title={new Date(log.created_at).toLocaleString()}>
                {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
              </span>
            </div>
            {log.details?.changes && (
              <p className="text-sm text-muted-foreground">
                {formatChanges(log)}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
